import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { Home, Loader2, Sparkles, Ticket, User, Users } from "lucide-react";
import { startTransition, useCallback, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useConsumerEntitlements } from "@/lib/consumerEntitlements";
import { navigateToDiscoveryTab, leaveDiscoveryTab, useDiscoverySpinnerVisible } from "@/lib/discoveryNavLoading";
import { cn } from "@/lib/utils";
import { isAiConciergeEnabled } from "@pulse/shared";

type NavKey = "discover" | "aiConcierge" | "circle" | "tickets" | "profile";

type TabDef = {
  to: "/" | "/concierge" | "/circle" | "/tickets" | "/profile";
  labelKey: NavKey;
  icon: typeof Home;
  exact: boolean;
  gated: false | "concierge" | "circle";
  /** Extra path prefixes that should keep this tab active (e.g. /squad under Circle). */
  alsoMatch?: readonly string[];
};

const tabs = [
  { to: "/", labelKey: "discover", icon: Home, exact: true, gated: false as const },
  { to: "/concierge", labelKey: "aiConcierge", icon: Sparkles, exact: false, gated: "concierge" as const },
  {
    to: "/circle",
    labelKey: "circle",
    icon: Users,
    exact: false,
    gated: "circle" as const,
    // Squad Night detail lives at /squad/$id — Circle tab context
    alsoMatch: ["/squad"],
  },
  // Tickets is a product-status screen (not tier-gated) — never dim the tab.
  { to: "/tickets", labelKey: "tickets", icon: Ticket, exact: false, gated: false as const },
  {
    to: "/profile",
    labelKey: "profile",
    icon: User,
    exact: false,
    gated: false as const,
    alsoMatch: ["/settings"],
  },
] as const satisfies ReadonlyArray<TabDef>;

type TabPath = (typeof tabs)[number]["to"];

function isTabActive(path: string, tab: TabDef): boolean {
  if (tab.exact) {
    return path === tab.to || path === "";
  }
  if (path === tab.to || path.startsWith(`${tab.to}/`)) return true;
  return (tab.alsoMatch ?? []).some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function TabBar() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useTranslation("consumer");
  const { entitlements } = useConsumerEntitlements();
  const conciergeEnabled = !!entitlements && isAiConciergeEnabled(entitlements);
  const circleEnabled = entitlements?.circleEnabled !== false;
  const [pressedTab, setPressedTab] = useState<TabPath | null>(null);
  const showDiscoverSpinner = useDiscoverySpinnerVisible();

  const clearPressedTab = useCallback(() => setPressedTab(null), []);

  const handleTabPress = useCallback(
    (to: TabPath) => {
      setPressedTab(to);
      // Do not begin Discovery loading here — pointerdown without click (cancelled
      // touch / scroll) would spin for up to 8s while staying on the current tab.
      void router.preloadRoute({ to });
    },
    [router],
  );

  const handleTabNavigate = useCallback(
    (to: TabPath, event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      // Already on this tab's own route tree — stay put.
      // alsoMatch routes (e.g. /squad → Circle) still navigate to the tab root.
      if (path === to || (to === "/" && path === "/")) return;
      if (to !== "/" && path.startsWith(`${to}/`)) return;
      if (to === "/") {
        navigateToDiscoveryTab(() => {
          startTransition(() => {
            void navigate({ to });
          });
        });
        return;
      }
      leaveDiscoveryTab();
      startTransition(() => {
        void navigate({ to });
      });
    },
    [navigate, path],
  );

  return (
    <nav className="fixed bottom-0 left-1/2 z-50 w-full max-w-md -translate-x-1/2 px-4 pb-4">
      <div
        className="flex items-center justify-around rounded-3xl px-2 py-2 shadow-card backdrop-blur-xl"
        style={{
          border: "1px solid var(--vc-hairline, rgba(245,246,248,0.12))",
          background: "color-mix(in srgb, var(--vc-surface, #141418) 88%, transparent)",
        }}
      >
        {tabs.map((tab) => {
          const { to, labelKey, icon: Icon, gated } = tab;
          const label = t(`nav.${labelKey}`);
          const active = isTabActive(path, tab);
          const highlighted = active || pressedTab === to;
          const gateOpen =
            gated === false
              ? true
              : gated === "concierge"
                ? conciergeEnabled
                : circleEnabled;
          // Dim Free-gated tabs only when not currently on that tab
          const muted = !gateOpen && !active;

          return (
            <Link
              key={to}
              to={to}
              preload="intent"
              activeOptions={tab.exact ? { exact: true } : undefined}
              aria-current={active ? "page" : undefined}
              aria-busy={to === "/" && showDiscoverSpinner ? true : undefined}
              data-tour-tab={labelKey}
              onPointerDown={() => handleTabPress(to)}
              onPointerUp={clearPressedTab}
              onPointerLeave={clearPressedTab}
              onPointerCancel={clearPressedTab}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => handleTabNavigate(to, event)}
              className={cn(
                "flex min-h-11 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 transition-all",
                muted && "opacity-45",
              )}
              style={
                highlighted
                  ? {
                      background:
                        "linear-gradient(135deg, rgba(245,246,248,0.16), rgba(201,205,213,0.08))",
                      boxShadow: "0 0 0 1px rgba(245,246,248,0.14)",
                    }
                  : undefined
              }
            >
              {to === "/" && showDiscoverSpinner ? (
                <Loader2
                  className="h-5 w-5 animate-spin"
                  style={{ color: "var(--vc-silver-glow, #f5f6f8)" }}
                  strokeWidth={2.5}
                  aria-hidden
                />
              ) : (
                <Icon
                  className="h-5 w-5 transition-colors"
                  style={{
                    color: highlighted
                      ? "var(--vc-silver-glow, #f5f6f8)"
                      : "var(--vc-steel, #8b909a)",
                  }}
                  strokeWidth={highlighted ? 2.5 : 2}
                />
              )}
              <span
                className="text-[10px] font-medium uppercase tracking-wider transition-colors"
                style={{
                  color: highlighted
                    ? "var(--vc-platinum, #edeef1)"
                    : "var(--vc-steel-dim, #6b7078)",
                  fontFamily: "var(--vc-font-mono, inherit)",
                }}
              >
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
